import { useEffect, useRef } from 'react'

/**
 * Back4App bills every request the server makes on a client's behalf, so a poll that reaches an
 * authenticated route may not run faster than this. A person who wants fresher data has a
 * manual refresh.
 */
export const VISIBLE_POLL_MIN_INTERVAL_MS = 2 * 60 * 1000

/** After failures the wait grows (x2 each time) but never past this. */
export const VISIBLE_POLL_MAX_BACKOFF_MS = 15 * 60 * 1000

/** Resolve `false` (or throw) to report a failed tick; anything else is a success. */
export type VisiblePollTick = () => Promise<boolean | void> | boolean | void

/**
 * Runs `tick` repeatedly under the Back4App poll rule: never faster than
 * `VISIBLE_POLL_MIN_INTERVAL_MS`, never while the tab is hidden, never while the previous tick is
 * still running (the next wait starts when the tick ends), and with growing waits after failures.
 * Does not tick at start: the caller does its own first load. Returns the stop function.
 */
export function startVisiblePoll(
  tick: VisiblePollTick,
  intervalMs: number = VISIBLE_POLL_MIN_INTERVAL_MS
): () => void {
  const baseMs = Math.max(intervalMs, VISIBLE_POLL_MIN_INTERVAL_MS)
  let stopped = false
  let failures = 0
  let timer: ReturnType<typeof setTimeout> | null = null

  const schedule = () => {
    if (stopped) return
    const waitMs = Math.min(baseMs * 2 ** failures, VISIBLE_POLL_MAX_BACKOFF_MS)
    timer = setTimeout(run, waitMs)
  }

  const run = async () => {
    timer = null
    if (stopped) return
    // A hidden tab is skipped, not queued: nobody is looking, and returning to the tab gets the
    // next regular tick.
    if (typeof document !== 'undefined' && document.hidden) {
      schedule()
      return
    }
    try {
      const ok = await tick()
      failures = ok === false ? failures + 1 : 0
    } catch {
      failures += 1
    }
    schedule()
  }

  schedule()
  return () => {
    stopped = true
    if (timer) clearTimeout(timer)
    timer = null
  }
}

/**
 * `startVisiblePoll` for a component: polls while `enabled`, keeps the latest `tick` without
 * restarting the wait on every render.
 */
export function useVisiblePoll(
  tick: VisiblePollTick,
  enabled: boolean,
  intervalMs: number = VISIBLE_POLL_MIN_INTERVAL_MS
): void {
  const tickRef = useRef(tick)
  tickRef.current = tick

  useEffect(() => {
    if (!enabled) return undefined
    return startVisiblePoll(() => tickRef.current(), intervalMs)
  }, [enabled, intervalMs])
}
